import {
  useCallback,
  useEffect,
  useRef,
  useState } from 'react';
import {
  Badge,
  Empty,
  Field,
  Grid,
  Note,
  Panel,
  ShieldIcon,
  Spinner,
  StateBadge,
  Stat,
  TableWrap,
  shortId,
} from '@wyocspay/ui';
import {
  badgeLabel,
  dateTime,
  methodLabel,
  money,
  moneyHeadline,
  moneyList,
  reasonLabel,
  relative,
  settingDescription,
  settingLabel,
} from './format.js';
import { COUNTRIES, CURRENCIES, countryName, currencyName } from '@wyocspay/shared/regions';
import { ApiError, api, type FeeModel, type MerchantCurrencyAdvice } from './api.js';
import { LogoCropModal } from './LogoCropModal.js';
import { buildCommand, requireIdentity, signCommand, type DeviceIdentity } from './device.js';

/**
 * Platform oversight views.
 *
 * Read endpoints are ordinary admin-session requests. Anything that changes
 * state - repricing a merchant, freezing a user, refunding a charge, flipping
 * a platform switch - goes through a signed control command, so the record of
 * who did it is cryptographic rather than "така пишеше в таблицата с потребители".
 */

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  // Live: refresh in the background every few seconds so statuses, transactions
  // and every other list update without reloading the page. Silent (no spinner),
  // and paused while the tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

function Frame({ loading, error, children }: { loading: boolean; error: string | null; children: React.ReactNode }) {
  if (loading) return <Spinner label="Зареждане…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  return <>{children}</>;
}

export interface Signer {
  /**
   * Null when this browser holds no signing key.
   *
   * Reaching the console now takes a client certificate rather than a device
   * key, so a browser can legitimately be signed in with nothing to sign with.
   * Reads work; anything that changes state is refused here with an
   * explanation rather than throwing on a null.
   */
  identity: DeviceIdentity | null;
  actorId: string;
}

/** x402 values are protocol settlement settings, not Stripe account fields. */
const X402_NETWORK_OPTIONS = [
  { value: 'eip155:8453', label: 'Base mainnet', detail: 'Live USDC with low fees; recommended for production.' },
  { value: 'eip155:84532', label: 'Base Sepolia', detail: 'Test network only; no real funds.' },
  { value: 'eip155:1', label: 'Ethereum mainnet', detail: 'Live USDC; gas costs are usually much higher.' },
  { value: 'solana:mainnet', label: 'Solana mainnet', detail: 'Live USDC; only if your facilitator supports Solana.' },
] as const;

const X402_TOKEN_OPTIONS = [
  { value: 'USDC', label: 'USDC', detail: 'USD Coin. The facilitator must support this asset on the selected network.' },
] as const;

/** One place that builds, signs and sends a command. */
async function send(
  signer: Signer,
  target: string,
  transition: string,
  previousVersion: number,
  params: Record<string, unknown> = {},
) {
  const identity = requireIdentity(signer.identity);
  const cmd = buildCommand({
    actorId: signer.actorId,
    deviceId: identity.deviceId,
    target,
    transition,
    previousVersion,
    params,
  });
  return api.command(cmd, await signCommand(identity, cmd));
}

// ---------------------------------------------------------------------------

export function ReviewQueueView({ onOpenMerchant }: { onOpenMerchant: (id: string) => void }) {
  const { data, error, loading } = useLoad(() => api.queue());

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Изведено от Stripe, а не поддържано на ръка">
        Търговецът се появява тук заради това, което Stripe в момента казва за профила му. Нищо не може да стои в остаряло състояние „чака преглед“, което вече не отговаря на реалността.
      </Note>

      <Frame loading={loading} error={error}>
        <Panel padded={false}>
          {data && data.data.length > 0 ? (
            <TableWrap>
              <table className="wc-table">
                <thead>
                  <tr><th>Приоритет</th><th>Търговец</th><th>Защо е тук</th><th>Чака</th><th /></tr>
                </thead>
                <tbody>
                  {data.data.map((item) => (
                    <tr key={item.merchant.id}>
                      <td>
                        <Badge tone={item.priority === 'high' ? 'fail' : item.priority === 'medium' ? 'warn' : 'neutral'}>
                          {badgeLabel(item.priority)}
                        </Badge>
                      </td>
                      <td>
                        <strong>{item.merchant.name}</strong>
                        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{item.merchant.email}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {item.reasons.map((r, i) => (
                            <li key={i}>{reasonLabel(item.reason_codes?.[i], r)}</li>
                          ))}
                        </ul>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>
                        {relative(item.created_at)}
                      </td>
                      <td className="wc-right">
                        <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => onOpenMerchant(item.merchant.id)}>
                          Отвори
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Empty>Нищо не чака решение.</Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function MerchantDetailView({
  id,
  signer,
  onBack,
}: {
  id: string;
  signer: Signer;
  onBack: () => void;
}) {
  const { data, error, loading, reload } = useLoad(() => api.merchant(id), [id]);
  const machineAccess = useLoad(() => api.machinePayments(id), [id]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [editFee, setEditFee] = useState(false);
  const [bps, setBps] = useState('');
  const [fixed, setFixed] = useState('');
  const [editProfile, setEditProfile] = useState(false);

  async function act(transition: string, params: Record<string, unknown> = {}): Promise<void> {
    if (!data) return;
    setBusy(true);
    setMsg(null);
    try {
      await send(signer, `merchant:${id}`, transition, data.merchant.version, params);
      setMsg({ tone: 'ok', text: `${transition} - приложено и записано.` });
      setEditFee(false);
      setEditProfile(false);
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function actUser(userId: string, transition: string): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const res = await send(signer, `user:${userId}`, transition, 0, { reason: 'административно действие' });
      setMsg({
        tone: 'ok',
        text: transition === 'freeze'
          ? `Потребителят е замразен. Прекратени сесии: ${res.new_version}.`
          : 'Потребителят е размразен.',
      });
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack} style={{ justifySelf: 'start' }}>
        ← Назад
      </button>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Grid cols={4}>
              <Stat label="Уреден оборот" value={money(data.totals.volume, data.merchant.currency)} accent="var(--wc-green)" />
              <Stat label="Спечелени такси" value={money(data.totals.fees, data.merchant.currency)} />
              <Stat label="Рефундирано" value={money(data.totals.refunded, data.merchant.currency)} accent="var(--wc-blue)" />
              <Stat
                label="Статус"
                value={
                  <Badge tone={data.merchant.status === 'ACTIVE' ? 'ok' : data.merchant.status === 'SUSPENDED' ? 'fail' : 'pending'}>
                    {badgeLabel(data.merchant.status)}
                  </Badge>
                }
                hint={`version ${data.merchant.version}`}
                accent="var(--wc-grey)"
              />
            </Grid>

            <Grid cols={2}>
              <Panel title="Профил в Stripe">
                {data.stripe_account ? (
                  <dl style={{ margin: 0, display: 'grid', gap: 9 }}>
                    <KV k="Профил" v={<span className="wc-num" style={{ fontSize: 12.5 }}>{data.stripe_account.id}</span>} />
                    <KV k="Приемане на плащания" v={<Badge tone={data.stripe_account.charges_enabled ? 'ok' : 'fail'}>{badgeLabel(data.stripe_account.charges_enabled ? 'ENABLED' : 'DISABLED')}</Badge>} />
                    <KV k="Изплащания" v={<Badge tone={data.stripe_account.payouts_enabled ? 'ok' : 'fail'}>{badgeLabel(data.stripe_account.payouts_enabled ? 'ENABLED' : 'DISABLED')}</Badge>} />
                    <KV k="Подадени данни" v={data.stripe_account.details_submitted ? 'Да' : 'No'} />
                    {data.stripe_account.requirements.disabled_reason ? (
                      <KV k="Причина за спиране" v={<span style={{ color: 'var(--wc-red)' }}>{data.stripe_account.requirements.disabled_reason}</span>} />
                    ) : null}
                    {data.stripe_account.requirements.currently_due.length > 0 ? (
                      <div style={{ marginTop: 6 }}>
                        <Note tone="warn" title="Stripe чака">
                          {data.stripe_account.requirements.currently_due.join(', ')}
                          {data.stripe_account.requirements.current_deadline ? (
                            <div style={{ marginTop: 4, fontSize: 12.5 }}>
                              Deadline {dateTime(data.stripe_account.requirements.current_deadline)}
                            </div>
                          ) : null}
                        </Note>
                      </div>
                    ) : null}
                    <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--wc-faint)' }}>
                      Capabilities:{' '}
                      {Object.entries(data.stripe_account.capabilities)
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ') || 'няма'}
                    </div>
                  </dl>
                ) : (
                  <Empty>
                    {data.stripe_configured
                      ? 'Още няма профил в Stripe - търговецът не е започнал верификацията.'
                      : 'Stripe не е конфигуриран, затова състоянието на профила на живо не е достъпно.'}
                  </Empty>
                )}
              </Panel>

              <MerchantFeeRulePanel
                fee={data.merchant.fee}
                platformDefault={data.platform_default_fee}
                feeModel={data.fee_model}
                feeMode={data.merchant.fee_mode}
                subscriptionTier={data.merchant.subscription_tier}
                currency={data.merchant.currency}
                editing={editFee}
                busy={busy}
                bps={bps}
                fixed={fixed}
                onBps={setBps}
                onFixed={setFixed}
                onEdit={(nextBps, nextFixed) => {
                  setBps(String(nextBps));
                  setFixed(String(nextFixed));
                  setEditFee(true);
                }}
                onCancel={() => setEditFee(false)}
                onApply={() => act('set_fee', { percent_bps: Number(bps), fixed: Number(fixed) })}
                onRevertStatic={() => act('set_fee', { mode: 'static' })}
              />
            </Grid>

            <MerchantProfilePanel
              merchant={data.merchant}
              editing={editProfile}
              busy={busy}
              onEdit={() => setEditProfile(true)}
              onCancel={() => setEditProfile(false)}
              onSave={(fields) => act('set_profile', fields)}
            />

            <MerchantCurrencyPanel
              advice={data.currency_advice}
              country={data.merchant.country}
              busy={busy}
              onApply={(currency) => act('set_currency', { currency })}
            />

            <MerchantFeeModelPanel model={data.fee_model} />

            <MachineApprovalPanel
              data={machineAccess.data}
              loading={machineAccess.loading}
              busy={busy}
              onApprove={async (body) => { setBusy(true); setMsg(null); try { await api.approveMachinePayments(id, body); setMsg({ tone: 'ok', text: 'Machine payments access is approved and audited.' }); await machineAccess.reload(); } catch (err) { setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) }); } finally { setBusy(false); } }}
              onReject={async (reason) => { setBusy(true); setMsg(null); try { await api.rejectMachinePayments(id, reason); setMsg({ tone: 'ok', text: 'Machine payments request rejected.' }); await machineAccess.reload(); } catch (err) { setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) }); } finally { setBusy(false); } }}
              onRevoke={async (reason) => { setBusy(true); setMsg(null); try { await api.revokeMachinePayments(id, reason); setMsg({ tone: 'ok', text: 'Machine payments access revoked.' }); await machineAccess.reload(); } catch (err) { setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) }); } finally { setBusy(false); } }}
            />

            <Panel title="Акаунт на платформата (собственик)">
              <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--wc-muted)' }}>
                Когато е включено, плащанията към този акаунт се таксуват директно през платформения
                Stripe (без connect акаунт, без такса към платформата) - само таксата на Stripe. Използва
                се единствено за твоя собствен акаунт; за всички други остава изключено.
              </p>
              <label style={{ display: 'flex', gap: 9, alignItems: 'center', cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={data.merchant.platform_owned}
                  disabled={busy}
                  onChange={(e) => act('set_platform_owned', { enabled: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: 'var(--wc-blue)' }}
                />
                Това е моят собствен акаунт (плащай директно през платформения Stripe)
              </label>
            </Panel>

            <MerchantLogoPanel merchantId={id} logo={data.logo} onChanged={() => void reload()} />

            <Panel title="Действия по профила">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {data.merchant.status === 'SUSPENDED' ? (
                  <button type="button" className="wc-btn wc-btn-sm" disabled={busy} onClick={() => act('reinstate', { reason: 'прегледано и изчистено' })}>
                    Възстанови
                  </button>
                ) : data.merchant.status !== 'CLOSED' ? (
                  <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" disabled={busy} onClick={() => act('suspend', { reason: 'преглед на риска' })}>
                    Спри
                  </button>
                ) : null}
                {data.merchant.status !== 'CLOSED' ? (
                  <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={() => act('close', { reason: 'закрито от администратор' })}>
                    Закрий профила
                  </button>
                ) : null}
              </div>
              <p style={{ marginBottom: 0, marginTop: 12, fontSize: 12.5, color: 'var(--wc-faint)' }}>
                Спирането прекратява и всяка активна сесия на потребителите на този търговец.
              </p>
            </Panel>

            <Panel title="Потребители" padded={false}>
              <TableWrap>
                <table className="wc-table">
                  <thead><tr><th>Име</th><th>Имейл</th><th>Роля</th><th>Статус</th><th /></tr></thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td style={{ fontSize: 13 }}>{u.email}</td>
                        <td className="wc-num" style={{ fontSize: 12 }}>{badgeLabel(u.role)}</td>
                        <td><Badge tone={u.status === 'ACTIVE' ? 'ok' : 'fail'}>{badgeLabel(u.status)}</Badge></td>
                        <td className="wc-right">
                          <button
                            type="button"
                            className={`wc-btn wc-btn-sm ${u.status === 'ACTIVE' ? 'wc-btn-danger' : 'wc-btn-secondary'}`}
                            disabled={busy}
                            onClick={() => actUser(u.id, u.status === 'ACTIVE' ? 'freeze' : 'unfreeze')}
                          >
                            {u.status === 'ACTIVE' ? 'Замрази' : 'Размрази'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            </Panel>

            <Panel title="История">
              <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {data.timeline.slice(-25).reverse().map((t, i) => (
                  <li key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--wc-border)', fontSize: 13 }}>
                    <span className="wc-num" style={{ color: 'var(--wc-faint)', fontSize: 11.5 }}>{dateTime(t.at)}</span>
                    <span>
                      <strong>{t.action}</strong> <Badge tone="neutral">{t.actor}</Badge>
                    </span>
                  </li>
                ))}
              </ol>
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

function MachineApprovalPanel({
  data,
  loading,
  busy,
  onApprove,
  onReject,
  onRevoke,
}: {
  data: Awaited<ReturnType<typeof api.machinePayments>> | null;
  loading: boolean;
  busy: boolean;
  onApprove: (body: { mpp_enabled: boolean; x402_enabled: boolean; x402_pay_to?: string | null; x402_network?: string | null; x402_token?: string | null }) => Promise<void>;
  onReject: (reason: string) => Promise<void>;
  onRevoke: (reason: string) => Promise<void>;
}) {
  const [mpp, setMpp] = useState(false);
  const [x402, setX402] = useState(false);
  const [payTo, setPayTo] = useState('');
  const [network, setNetwork] = useState('eip155:8453');
  const [token, setToken] = useState('USDC');
  useEffect(() => {
    if (!data) return;
    setMpp(data.protocols.mpp); setX402(data.protocols.x402); setPayTo(data.x402.pay_to ?? ''); setNetwork(data.x402.network ?? 'eip155:8453'); setToken(data.x402.token ?? 'USDC');
  }, [data]);
  return <Panel title="Machine payments approval">
    {loading ? <Spinner label="Зареждане…" /> : data ? <>
      <p style={{ margin: '0 0 12px', color: 'var(--wc-muted)', fontSize: 13, lineHeight: 1.5 }}>Това е отделно разрешение за MPP/x402. То не се наследява от обикновен API key. При одобрение ресурсите се насочват само към този merchant Connect account.</p>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
        <label><input type="checkbox" checked={mpp} disabled={busy} onChange={(e) => setMpp(e.target.checked)} /> MPP / Stripe</label>
        <label><input type="checkbox" checked={x402} disabled={busy} onChange={(e) => setX402(e.target.checked)} /> x402 / own facilitator</label>
      </div>
      {x402 ? <Grid cols={3}>
        <Field label="x402 pay-to address" hint="Блокчейн портфейлът на търговеца за получаване на USDC. Не е Stripe Connect адрес, API ключ или pay.wyocs.org линк.">
          <input className="wc-input" value={payTo} disabled={busy} inputMode="text" autoComplete="off" placeholder="0x… (Base/Ethereum) или Solana адрес" onChange={(e) => setPayTo(e.target.value)} />
        </Field>
        <Field label="Network" hint="Изберете мрежа, за която портфейлът и вашият facilitator действително поддържат плащания.">
          <select className="wc-input" value={network} disabled={busy} onChange={(e) => setNetwork(e.target.value)}>
            {!X402_NETWORK_OPTIONS.some((option) => option.value === network) && network ? <option value={network}>Текуща конфигурация ({network})</option> : null}
            {X402_NETWORK_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label} · {option.value}</option>)}
          </select>
          <span style={{ display: 'block', marginTop: 5, color: 'var(--wc-muted)', fontSize: 12 }}>{X402_NETWORK_OPTIONS.find((option) => option.value === network)?.detail ?? 'Провери тази CAIP-2 мрежа във facilitator-а преди одобрение.'}</span>
        </Field>
        <Field label="Token" hint="Това е активът в блокчейна, не валутата на Stripe ресурса.">
          <select className="wc-input" value={token} disabled={busy} onChange={(e) => setToken(e.target.value)}>
            {!X402_TOKEN_OPTIONS.some((option) => option.value === token) && token ? <option value={token}>Текущ актив ({token})</option> : null}
            {X402_TOKEN_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <span style={{ display: 'block', marginTop: 5, color: 'var(--wc-muted)', fontSize: 12 }}>{X402_TOKEN_OPTIONS.find((option) => option.value === token)?.detail ?? 'Потвърди актива във facilitator-а.'}</span>
        </Field>
      </Grid> : null}
      {x402 ? <Note tone="warn" title="Преди одобрение">Ако търговецът няма собствен портфейл, остави x402 изключен. WyoCSPay не създава портфейли и не може да приеме pay-to адрес от pay.wyocs.org.</Note> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
        <button type="button" className="wc-btn wc-btn-primary wc-btn-sm" disabled={busy || (!mpp && !x402)} onClick={() => void onApprove({ mpp_enabled: mpp, x402_enabled: x402, x402_pay_to: x402 ? payTo : null, x402_network: x402 ? network : null, x402_token: x402 ? token : null })}>Одобри и запази</button>
        {data.status === 'PENDING' ? <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" disabled={busy} onClick={() => void onReject('Не е одобрено от оператора.')}>Откажи заявката</button> : null}
        {data.status === 'APPROVED' ? <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" disabled={busy} onClick={() => void onRevoke('Достъпът е спрян от оператора.')}>Отнеми достъпа</button> : null}
      </div>
      <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--wc-muted)' }}>Статус: <Badge tone={data.status === 'APPROVED' ? 'ok' : data.status === 'PENDING' ? 'pending' : data.status === 'REJECTED' ? 'fail' : 'neutral'}>{data.status}</Badge></div>
    </> : <Empty>Няма machine-payments заявка.</Empty>}
  </Panel>;
}

/**
 * Правилото за такса на този търговец.
 *
 * Таксата на платформата се задава за всеки търговец поотделно и може да бъде
 * нула - точно това е "освободен от таксата на платформата". Панелът показва и
 * стойността по подразбиране, за да се вижда дали числото е решение, или просто
 * онова, което всеки нов търговец получава.
 *
 * Ефектът е изписан с думи, а не оставен да се извежда от две числа: разликата
 * между "нула" и "по подразбиране" е разликата между това търговецът да плаща
 * само на Stripe и да плаща на Stripe плюс на платформата, а това не бива да се
 * налага да се смята наум.
 */
function MerchantFeeRulePanel({
  fee,
  platformDefault,
  feeModel,
  feeMode,
  subscriptionTier,
  currency,
  editing,
  busy,
  bps,
  fixed,
  onBps,
  onFixed,
  onEdit,
  onCancel,
  onApply,
  onRevertStatic,
}: {
  fee: { percent_bps: number; fixed: number };
  platformDefault: { percent_bps: number; fixed: number };
  feeModel: FeeModel;
  feeMode: string;
  subscriptionTier: string;
  currency: string;
  editing: boolean;
  busy: boolean;
  bps: string;
  fixed: string;
  onBps: (value: string) => void;
  onFixed: (value: string) => void;
  onEdit: (bps: number, fixed: number) => void;
  onCancel: () => void;
  onApply: () => void;
  onRevertStatic: () => void;
}) {
  const isDefault =
    fee.percent_bps === platformDefault.percent_bps && fee.fixed === platformDefault.fixed;
  const exempt = fee.percent_bps === 0 && fee.fixed === 0;
  const draftExempt = Number(bps) === 0 && Number(fixed) === 0;
  // A merchant on a package has a package-owned fee; hand-editing is refused by
  // the server, so the console does not offer it either.
  const onPackage = Boolean(subscriptionTier && subscriptionTier !== 'none');
  const isStatic = feeMode === 'static';

  /**
   * Какво означава числото на практика.
   *
   * Зависи и от това коя страна плаща на Stripe: при нулева такса на платформата
   * търговец на модела `account` пак плаща на Stripe, а търговец на модела
   * `application` не плаща нищо на никого. Двете изречения не са взаимозаменяеми.
   */
  const effect = exempt
    ? feeModel.payer === 'application'
      ? 'Този търговец не плаща нищо: платформата не удържа такса, а таксата на Stripe се поема от платформата.'
      : 'Този търговец плаща само таксата на Stripe. Платформата не удържа нищо от плащанията му.'
    : feeModel.payer === 'application'
      ? `Този търговец плаща само таксата на платформата - ${(fee.percent_bps / 100).toFixed(2)}%${fee.fixed > 0 ? ` + ${money(fee.fixed, currency)}` : ''} на плащане. Таксата на Stripe се поема от платформата.`
      : `Този търговец плаща таксата на Stripe плюс ${(fee.percent_bps / 100).toFixed(2)}%${fee.fixed > 0 ? ` + ${money(fee.fixed, currency)}` : ''} на платформата.`;

  return (
    <Panel
      title="Такса на платформата"
      actions={
        !editing && !onPackage ? (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={() => onEdit(fee.percent_bps, fee.fixed)}
          >
            Промяна
          </button>
        ) : null
      }
    >
      {!editing ? (
        <dl style={{ margin: 0, display: 'grid', gap: 9 }}>
          <KV k="Процент" v={`${(fee.percent_bps / 100).toFixed(2)}%`} />
          <KV k="Фиксирана на плащане" v={money(fee.fixed, currency)} />
          <KV
            k="Спрямо платформата"
            v={
              exempt ? (
                <Badge tone="info">ОСВОБОДЕН</Badge>
              ) : isDefault ? (
                <Badge tone="neutral">ПО ПОДРАЗБИРАНЕ</Badge>
              ) : (
                <Badge tone="warn">ИНДИВИДУАЛНА</Badge>
              )
            }
          />
          <KV
            k="Режим"
            v={
              onPackage ? (
                <Badge tone="info">ПАКЕТ ({subscriptionTier})</Badge>
              ) : isStatic ? (
                <Badge tone="neutral">ДИНАМИЧНА (следва подразбирането)</Badge>
              ) : (
                <Badge tone="warn">ЗАМРАЗЕНА (индивидуална)</Badge>
              )
            }
          />
          {onPackage ? (
            <Note tone="info">
              Таксата се управлява от активния пакет на този търговец и е нулирана. За да я
              промените ръчно, първо прекратете пакета в „Абонаменти“.
            </Note>
          ) : !isStatic ? (
            <div>
              <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={onRevertStatic}>
                Върни към динамична (по подразбиране за региона)
              </button>
              <div style={{ fontSize: 12, color: 'var(--wc-faint)', marginTop: 4 }}>
                Динамичната такса се обновява автоматично, когато смените таксата по подразбиране за
                региона в „Настройки“.
              </div>
            </div>
          ) : null}
          <div style={{ marginTop: 4 }}>
            <Note tone={exempt ? 'ok' : undefined}>{effect}</Note>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
            По подразбиране за нови търговци: {(platformDefault.percent_bps / 100).toFixed(2)}% +{' '}
            {money(platformDefault.fixed, currency)}. Промяната важи за плащания, създадени след нея -
            вече създадените запазват таксата, с която са направени.
          </div>
        </dl>
      ) : (
        <div>
          <div className="wc-label">Готови стойности</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 14px' }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-secondary"
              onClick={() => {
                onBps(String(platformDefault.percent_bps));
                onFixed(String(platformDefault.fixed));
              }}
            >
              По подразбиране ({(platformDefault.percent_bps / 100).toFixed(2)}%)
            </button>
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-secondary"
              onClick={() => {
                onBps('0');
                onFixed('0');
              }}
            >
              Без такса на платформата
            </button>
          </div>

          <Field label="Базисни точки" hint="100 = 1.00%. Нула означава, че платформата не удържа процент.">
            <input
              className="wc-input wc-num"
              value={bps}
              onChange={(e) => onBps(e.target.value)}
              inputMode="numeric"
            />
          </Field>
          <Field label="Фиксирана (в стотинки)" hint="30 = $0.30. Нула означава, че няма фиксирана част.">
            <input
              className="wc-input wc-num"
              value={fixed}
              onChange={(e) => onFixed(e.target.value)}
              inputMode="numeric"
            />
          </Field>

          <div style={{ marginBottom: 14 }}>
            <Note tone={draftExempt ? 'ok' : undefined} title="Какво ще означава това">
              {draftExempt
                ? feeModel.payer === 'application'
                  ? 'Търговецът няма да плаща нищо - нито на платформата, нито на Stripe.'
                  : 'Търговецът ще плаща само таксата на Stripe. Платформата няма да удържа нищо.'
                : `Търговецът ще плаща ${(Number(bps) / 100 || 0).toFixed(2)}%${Number(fixed) > 0 ? ` + ${money(Number(fixed), currency)}` : ''} на платформата${feeModel.payer === 'application' ? ' и нищо на Stripe' : ', плюс таксата на Stripe'}.`}
            </Note>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="wc-btn wc-btn-sm" disabled={busy} onClick={onApply}>
              <ShieldIcon size={13} /> Подпиши и приложи
            </button>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={onCancel}>
              Отказ
            </button>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
            Смяната на цената е подписана команда и се задържа за втори администратор, освен ако вашият
            профил не може да подписва сам.
          </p>
        </div>
      )}
    </Panel>
  );
}

/**
 * Данните на клиента, така както платформата ги води.
 *
 * Промяната е подписана команда като всичко останало по този търговец - няма
 * втори, по-лек път за същото действие. Изпращат се само променените полета, за
 * да не изтрие едно поле останалите.
 */
function MerchantProfilePanel({
  merchant,
  editing,
  busy,
  onEdit,
  onCancel,
  onSave,
}: {
  merchant: {
    name: string;
    email: string;
    country: string;
    contact: { phone: string | null; address: string | null };
  };
  editing: boolean;
  busy: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (fields: Record<string, string>) => void;
}) {
  const [name, setName] = useState(merchant.name);
  const [email, setEmail] = useState(merchant.email);
  const [country, setCountry] = useState(merchant.country);
  const [phone, setPhone] = useState(merchant.contact.phone ?? '');
  const [address, setAddress] = useState(merchant.contact.address ?? '');

  /**
   * Формата се пълни наново всеки път, когато се отвори.
   *
   * Иначе едно отменено редактиране оставя старите чернови и следващото
   * отваряне показва стойности, които никой не е въвеждал сега.
   */
  useEffect(() => {
    if (!editing) return;
    setName(merchant.name);
    setEmail(merchant.email);
    setCountry(merchant.country);
    setPhone(merchant.contact.phone ?? '');
    setAddress(merchant.contact.address ?? '');
  }, [editing, merchant]);

  const valid = name.trim().length >= 2 && email.includes('@');

  return (
    <Panel
      title="Данни на търговеца"
      actions={
        !editing ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={onEdit}>
            Промяна
          </button>
        ) : null
      }
    >
      {!editing ? (
        <dl style={{ margin: 0, display: 'grid', gap: 9 }}>
          <KV k="Фирма" v={merchant.name} />
          <KV k="Имейл" v={merchant.email} />
          <KV k="Държава" v={`${countryName(merchant.country, 'bg')} (${merchant.country})`} />
          <KV k="Телефон" v={merchant.contact.phone ?? <span style={{ color: 'var(--wc-faint)' }}>не е попълнен</span>} />
          <KV k="Адрес" v={merchant.contact.address ?? <span style={{ color: 'var(--wc-faint)' }}>не е попълнен</span>} />
          <div style={{ fontSize: 12.5, color: 'var(--wc-faint)', marginTop: 4 }}>
            Това са данните на платформата за клиента, не проверена самоличност. Проверката е на Stripe
            и документите отиват само там.
          </div>
        </dl>
      ) : (
        <div>
          <Field label="Име на фирмата">
            <input className="wc-input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field
            label="Имейл"
            hint="Адресът, на който платформата пише на този бизнес. Смяната се записва в одитния дневник със старата и новата стойност."
          >
            <input className="wc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Държава" hint="Къде е регистриран бизнесът">
              <select className="wc-input" value={country} onChange={(e) => setCountry(e.target.value)}>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {countryName(c.code, 'bg')} ({c.code})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Телефон" hint="По желание. На кого да се звъни при неуспешно изплащане.">
              <input className="wc-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
            </Field>
          </div>
          <Field label="Адрес" hint="По желание, на един ред.">
            <input className="wc-input" value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>

          {!valid ? (
            <div style={{ marginBottom: 12 }}>
              <Note tone="warn">Нужни са име от поне два знака и валиден имейл адрес.</Note>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              disabled={busy || !valid}
              onClick={() =>
                onSave({
                  name: name.trim(),
                  email: email.trim(),
                  country,
                  contact_phone: phone.trim(),
                  contact_address: address.trim(),
                })
              }
            >
              <ShieldIcon size={13} /> Подпиши и запази
            </button>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={onCancel}>
              Отказ
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}

/**
 * Currency is inferred at registration and remains an operator-controlled
 * default afterwards. The panel puts the three relevant facts side by side so
 * changing it is a reviewable decision rather than a blind select box.
 */
function MerchantCurrencyPanel({
  advice,
  country,
  busy,
  onApply,
}: {
  advice: MerchantCurrencyAdvice;
  country: string;
  busy: boolean;
  onApply: (currency: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [currency, setCurrency] = useState(advice.recommended_currency);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    setCurrency(advice.recommended_currency);
    setConfirmed(false);
    setEditing(false);
  }, [advice.profile_currency, advice.recommended_currency]);

  const label = (code: string | null) =>
    code ? `${currencyName(code, 'bg')} (${code.toUpperCase()})` : 'няма стойност';
  const stripeVerified = advice.stripe_check_status === 'verified';
  const statusText = stripeVerified
    ? 'Проверено директно в live Stripe'
    : advice.stripe_check_status === 'not_connected'
      ? 'Още няма live Stripe профил'
      : 'Live Stripe проверката временно не е достъпна';

  return (
    <Panel
      title="Основна валута"
      actions={
        !editing ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => setEditing(true)}>
            Промяна
          </button>
        ) : null
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 10,
          marginBottom: 12,
        }}
      >
        {[
          { title: 'WyoCSPay профил', value: label(advice.profile_currency) },
          {
            title: 'Live Stripe',
            value: stripeVerified ? label(advice.stripe_currency) : statusText,
          },
          {
            title: `По държава · ${countryName(country, 'bg')}`,
            value: label(advice.country_currency),
          },
        ].map((item) => (
          <div
            key={item.title}
            style={{
              border: '1px solid var(--wc-border)',
              borderRadius: 8,
              padding: '11px 12px',
              background: 'var(--wc-surface-2, #f8fafc)',
              minWidth: 0,
            }}
          >
            <div style={{ color: 'var(--wc-faint)', fontSize: 11.5, marginBottom: 4 }}>{item.title}</div>
            <strong style={{ fontSize: 13.5 }}>{item.value}</strong>
          </div>
        ))}
      </div>

      <Note
        tone={advice.profile_matches_recommendation ? 'ok' : 'warn'}
        title={advice.profile_matches_recommendation ? 'Валутата е правилно зададена' : 'Препоръчва се промяна'}
      >
        {advice.profile_matches_recommendation ? (
          <>
            Профилът използва {label(advice.recommended_currency)} и съвпада с{' '}
            {advice.recommendation_source === 'stripe' ? 'live Stripe профила' : 'валутата за регистрираната държава'}.
          </>
        ) : (
          <>
            Задайте {label(advice.recommended_currency)} според{' '}
            {advice.recommendation_source === 'stripe' ? 'проверения live Stripe профил' : 'регистрираната държава'}.
          </>
        )}
        <div style={{ marginTop: 5, fontSize: 12 }}>{statusText}.</div>
      </Note>

      {editing ? (
        <div style={{ marginTop: 14 }}>
          <Field
            label="Нова основна валута"
            hint="Избира се автоматично при регистрация. Тук може да бъде коригирана от администратор след проверка."
          >
            <select className="wc-input" value={currency} onChange={(event) => {
              setCurrency(event.target.value);
              setConfirmed(false);
            }}>
              {CURRENCIES.filter((item) => advice.supported_currencies.includes(item.code)).map((item) => (
                <option key={item.code} value={item.code}>
                  {currencyName(item.code, 'bg')} ({item.code.toUpperCase()})
                  {item.code === advice.recommended_currency ? ' · препоръчана' : ''}
                </option>
              ))}
            </select>
          </Field>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              style={{ width: 16, height: 16, marginTop: 1, accentColor: 'var(--wc-blue)' }}
            />
            <span>
              Потвърждавам, че промяната важи само за нови плащания, линкове и операции. Съществуващите
              плащания запазват своята валута и валутата на изплащанията в Stripe не се променя.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              disabled={busy || !confirmed || currency === advice.profile_currency}
              onClick={() => onApply(currency)}
            >
              <ShieldIcon size={13} /> Подпиши и задай {currency.toUpperCase()}
            </button>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => setEditing(false)}>
              Отказ
            </button>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Кой плаща на Stripe за този профил.
 *
 * Показва се като факт, не като настройка, и нарочно няма превключвател. Stripe
 * не приема `controller` при обновяване на профил - параметърът просто не
 * съществува в POST /v1/accounts/:id - така че бутон тук би изпратил поле, което
 * никоя заявка не носи, нищо нямаше да се промени в Stripe, а конзолата щеше да
 * твърди нещо невярно за профила. Смяната минава през нов свързан профил и нова
 * верификация.
 */
function MerchantFeeModelPanel({ model }: { model: FeeModel }) {
  const platformPays = model.payer === 'application';

  return (
    <Panel title="Модел на таксите">
      <dl style={{ margin: 0, display: 'grid', gap: 9 }}>
        <KV
          k="Stripe таксува"
          v={
            <Badge tone={platformPays ? 'warn' : 'neutral'}>
              {platformPays ? 'ПЛАТФОРМАТА' : 'ТЪРГОВЕЦА'}
            </Badge>
          }
        />
        <KV k="controller.fees.payer" v={<span className="wc-num" style={{ fontSize: 12.5 }}>{model.payer}</span>} />
        <KV
          k="1099 формуляри"
          v={model.files_1099 === 'stripe' ? 'Издава ги Stripe' : 'Подава ги платформата'}
        />
      </dl>

      <div style={{ marginTop: 12 }}>
        <Note tone={platformPays ? 'warn' : undefined}>
          {platformPays
            ? 'Платформата поема таксите на Stripe за обработка и Connect за този профил. Затова тук е възможен търговец, който не плаща абсолютно нищо - но разходът не изчезва, а остава за платформата, при ставка, която се мени според вида на картата и държавата на издаване. Платформата отговаря и за подаването на 1099 формулярите на този профил.'
            : 'Stripe удържа своята такса директно от профила на търговеца и не таксува платформата. Таксата на платформата е чиста печалба, а не покриване на разход. Stripe издава 1099-K за този профил.'}
        </Note>
      </div>

      <div style={{ marginTop: 12 }}>
        <Note tone="warn" title="Не може да се промени за този профил">
          Изборът се прави веднъж - при създаването на свързания профил в Stripe.{' '}
          {model.locked
            ? 'Профилът вече съществува, затова моделът е окончателен.'
            : 'Профилът в Stripe още не е създаден, затова моделът ще бъде приложен при създаването му.'}{' '}
          Stripe не приема <span className="wc-num">controller</span> при обновяване на профил, така че
          промяна на живо няма как да се направи. Единственият път е нов свързан профил и нова
          верификация, с нов баланс.
        </Note>
      </div>
    </Panel>
  );
}

/**
 * Логото на търговеца, така както го виждат неговите клиенти.
 *
 * Байтовете искат сесията, затова обикновен `src` към API-то би получил 401.
 * Изтеглят се и се показват през object URL - политиката на конзолата допуска
 * `blob:` за изображения - и се освобождават при подмяна и при напускане.
 */
function MerchantLogoPanel({
  merchantId,
  logo,
  onChanged,
}: {
  merchantId: string;
  logo: { present: boolean; mime_type: string | null; size: number | null; updated_at: string | null };
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  /** The file the administrator just picked, being cropped in the modal. */
  const [cropFile, setCropFile] = useState<File | null>(null);

  useEffect(() => {
    if (!logo.present) {
      setUrl(null);
      return;
    }
    let created: string | null = null;
    let cancelled = false;

    void api
      .merchantLogoImage(merchantId)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // `updated_at` се сменя при всяка замяна, което е и сигналът за презареждане.
  }, [merchantId, logo.present, logo.updated_at]);

  /**
   * Save the already-cropped, already-shrunk image the modal hands back. No size
   * check here: the crop modal reads the picked file locally and returns a small
   * WebP/PNG under the API's byte budget, so any source image - a big export, a
   * screenshot - works, and the administrator can frame it to fit, exactly as a
   * merchant can. That is the point: an admin should never be blocked by a file
   * that is a few kilobytes too large; it is resized for them.
   */
  async function saveCropped(base64: string): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.setMerchantLogo(merchantId, base64);
      setMsg({ tone: 'ok', text: 'Логото е заменено. Промяната е записана в одитния дневник като действие на платформата.' });
      onChanged();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
      setCropFile(null);
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.removeMerchantLogo(merchantId, 'свалено от администратор');
      setMsg({ tone: 'ok', text: 'Логото е свалено. Страницата за плащане показва само името на търговеца.' });
      onChanged();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Лого на търговеца">
      <p style={{ marginTop: 0, fontSize: 13, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
        Това е единственото нещо на страницата за плащане, което търговецът избира сам. Показва се до
        името му и е по желание - без лого страницата изглежда точно както преди. Платформата може да
        го замени или свали, без да чака съгласие: лого, което се окаже чужда марка, трябва да слиза
        веднага.
      </p>

      {logo.present ? (
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: 12,
            border: '1px solid var(--wc-border)',
            background: 'var(--wc-surface)',
          }}
        >
          {url ? (
            <img
              src={url}
              alt={`Лого на търговеца`}
              style={{ display: 'block', height: 40, maxWidth: 200, width: 'auto', objectFit: 'contain' }}
            />
          ) : null}
          <div style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
            {logo.mime_type} · {logo.size === null ? '' : `${Math.round(logo.size / 1024)} KB`}
            {logo.updated_at ? ` · качено ${relative(logo.updated_at)}` : ''}
          </div>
        </div>
      ) : (
        <Empty>Търговецът не е качил лого.</Empty>
      )}

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          // Open the crop/fit modal rather than uploading the raw file.
          if (file) setCropFile(file);
        }}
      />

      {cropFile ? (
        <LogoCropModal
          file={cropFile}
          onSave={saveCropped}
          onCancel={() => setCropFile(null)}
        />
      ) : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="wc-btn wc-btn-sm wc-btn-secondary"
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          {logo.present ? 'Замени' : 'Качи лого'}
        </button>
        {logo.present ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" disabled={busy} onClick={() => void remove()}>
            Свали логото
          </button>
        ) : null}
      </div>

      {msg ? (
        <div style={{ marginTop: 12 }}>
          <Note tone={msg.tone}>{msg.text}</Note>
        </div>
      ) : null}

      <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--wc-faint)', lineHeight: 1.5 }}>
        PNG, JPEG или WebP - всякакъв размер. Избери изображение и го изрежи/напасни в следващата стъпка;
        оптимизира се автоматично за страницата за плащане. Типът се определя по байтовете на самия файл.
        SVG не се приема - той е документ, който може да носи скрипт, а страницата за плащане не изпълнява нищо чуждо.
      </p>
    </Panel>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
      <dt style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{k}</dt>
      <dd style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--wc-ink)' }}>{v}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function PayoutOversightView() {
  const { data, error, loading, reload } = useLoad(() => api.payoutOversight());

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Чете се на живо от всеки свързан профил">
        Изплащанията са Stripe, който премества пари от баланса на търговеца към банката му. WyoCSPay не държи и не движи тези средства, затова този изглед пита Stripe, вместо да чете локално копие.
      </Note>

      <Frame loading={loading} error={error}>
        {data?.stripe_configured === false ? (
          <Note tone="warn">{data.note}</Note>
        ) : (
          <>
            {data && (data.failed ?? 0) > 0 ? (
              <Note tone="danger" title={`Неуспешни изплащания: ${data.failed}`}>
                Неуспешно изплащане обикновено значи, че банковите данни на търговеца са грешни или профилът е ограничен. Няма да им бъде платено, докато това не се реши.
              </Note>
            ) : null}

            <Panel
              padded={false}
              actions={
                <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => void reload()}>
                  Обнови
                </button>
              }
              title="Изплащания"
            >
              {data && data.data.length > 0 ? (
                <TableWrap>
                  <table className="wc-table">
                    <thead><tr><th>Търговец</th><th>Статус</th><th>Метод</th><th>Пристигане</th><th>Грешка</th><th className="wc-right">Сума</th></tr></thead>
                    <tbody>
                      {data.data.map((p) => (
                        <tr key={p.id}>
                          <td>{p.merchant.name}</td>
                          <td>
                            <Badge tone={p.status === 'paid' ? 'ok' : p.status === 'failed' ? 'fail' : 'pending'}>
                              {p.status.toUpperCase()}
                            </Badge>
                          </td>
                          <td style={{ fontSize: 13 }}>{methodLabel(p.method)}</td>
                          <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{dateTime(p.arrival_date)}</td>
                          <td style={{ fontSize: 12.5, color: 'var(--wc-red)' }}>
                            {p.failure_code ? <>{p.failure_code}<div>{p.failure_message}</div></> : '-'}
                          </td>
                          <td className="wc-right wc-num" style={{ fontWeight: 600 }}>{money(p.amount, p.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <Empty>Все още няма изплащания.</Empty>
              )}
            </Panel>
          </>
        )}
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function RefundsDisputesView({ signer, onOpen }: { signer: Signer; onOpen: (id: string) => void }) {
  const { data, error, loading } = useLoad(() => api.refundsOversight());
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note>
        Отворете плащане, за да го рефундирате, да подновите линка му или да го проверите отново срещу Stripe. Рефундите се задържат за втори администратор, преди каквото и да е да се задвижи.
      </Note>

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Panel title="Рефунди" padded={false}>
              {data.refunds.length > 0 ? (
                <TableWrap>
                  <table className="wc-table">
                    <thead><tr><th>Плащане</th><th>Търговец</th><th>Статус</th><th>Рефундирано</th><th className="wc-right">Сума</th></tr></thead>
                    <tbody>
                      {data.refunds.map((r) => (
                        <tr key={r.payment_id} onClick={() => onOpen(r.payment_id)} style={{ cursor: 'pointer' }}>
                          <td className="wc-num" style={{ fontSize: 12 }}>{shortId(r.payment_id)}</td>
                          <td>{r.merchant.name}</td>
                          <td><StateBadge state={r.status} label={badgeLabel(r.status)} /></td>
                          <td className="wc-num">
                            {money(r.amount_refunded, r.currency)}
                            {r.fully_refunded ? <Badge tone="info" icon={false}>ПЪЛЕН</Badge> : null}
                          </td>
                          <td className="wc-right wc-num">{money(r.amount, r.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <Empty>Няма рефунди.</Empty>
              )}
            </Panel>

            <Panel title="Спорове" padded={false}>
              {data.disputes.length > 0 ? (
                <TableWrap>
                  <table className="wc-table">
                    <thead><tr><th>Плащане</th><th>Търговец</th><th>Спор</th><th>Обновено</th><th className="wc-right">Сума</th><th /></tr></thead>
                    <tbody>
                      {data.disputes.map((d) => (
                        <tr key={d.payment_id}>
                          <td className="wc-num" style={{ fontSize: 12 }}>{shortId(d.payment_id)}</td>
                          <td>{d.merchant.name}</td>
                          <td>
                            <Badge tone={d.dispute_status === 'won' ? 'ok' : d.dispute_status === 'lost' ? 'fail' : 'warn'}>
                              {d.dispute_status.toUpperCase()}
                            </Badge>
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{relative(d.updated_at)}</td>
                          <td className="wc-right wc-num">{money(d.amount, d.currency)}</td>
                          <td className="wc-right">
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => onOpen(d.payment_id)}
                            >
                              Отвори
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <Empty>Няма спорове.</Empty>
              )}
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function RevenueView() {
  const [days, setDays] = useState(30);
  const { data, error, loading } = useLoad(() => api.revenue(days), [days]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {[7, 30, 90, 365].map((d) => (
          <button key={d} type="button" className={`wc-btn wc-btn-sm ${days === d ? '' : 'wc-btn-secondary'}`} onClick={() => setDays(d)}>
            {d} дни
          </button>
        ))}
      </div>

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            {data.total.length > 1 ? (
              <Note title="Няколко валути">
                Оборотът е в {data.total.length} валути. Сумите се показват поотделно - събирането
                им би дало число, което не отговаря на никаква реална сума.
              </Note>
            ) : null}

            <Grid cols={4}>
              <Stat
                label={`Такси · ${data.period_days} дни`}
                value={moneyHeadline(data.total.map((t) => ({ currency: t.currency, amount: t.fees }))).value}
                hint={moneyHeadline(data.total.map((t) => ({ currency: t.currency, amount: t.fees }))).hint ?? undefined}
                accent="var(--wc-green)"
              />
              <Stat
                label="Обработен оборот"
                value={moneyHeadline(data.total.map((t) => ({ currency: t.currency, amount: t.volume }))).value}
                hint={moneyHeadline(data.total.map((t) => ({ currency: t.currency, amount: t.volume }))).hint ?? undefined}
              />
              <Stat
                label="Рефундирано"
                value={moneyHeadline(data.total.map((t) => ({ currency: t.currency, amount: t.refunded }))).value}
                hint={moneyHeadline(data.total.map((t) => ({ currency: t.currency, amount: t.refunded }))).hint ?? undefined}
                accent="var(--wc-blue)"
              />
              <Stat
                label="Уредени плащания"
                value={data.total.reduce((n, t) => n + t.payments, 0)}
                accent="var(--wc-grey)"
              />
            </Grid>

            <Grid cols={2}>
              <Panel title="По търговец" padded={false}>
                {data.by_merchant.length > 0 ? (
                  <TableWrap>
                    <table className="wc-table">
                      <thead><tr><th>Търговец</th><th>Плащания</th><th className="wc-right">Оборот</th><th className="wc-right">Такси</th></tr></thead>
                      <tbody>
                        {data.by_merchant.map((m) => (
                          <tr key={m.key}>
                            <td>{m.label}</td>
                            <td className="wc-num">{m.payments}</td>
                            <td className="wc-right wc-num">{moneyList(m.volume)}</td>
                            <td className="wc-right wc-num" style={{ fontWeight: 600, color: 'var(--wc-green)' }}>{moneyList(m.fees)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                ) : <Empty>Няма уредени плащания за този период.</Empty>}
              </Panel>

              <Panel title="По метод на плащане" padded={false}>
                {data.by_method.length > 0 ? (
                  <TableWrap>
                    <table className="wc-table">
                      <thead><tr><th>Метод</th><th>Плащания</th><th className="wc-right">Оборот</th><th className="wc-right">Такси</th></tr></thead>
                      <tbody>
                        {data.by_method.map((m) => (
                          <tr key={m.key}>
                            <td>{methodLabel(m.key)}</td>
                            <td className="wc-num">{m.payments}</td>
                            <td className="wc-right wc-num">{moneyList(m.volume)}</td>
                            <td className="wc-right wc-num" style={{ fontWeight: 600 }}>{moneyList(m.fees)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                ) : <Empty>Няма данни.</Empty>}
              </Panel>
            </Grid>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Хората с профил, разделени по това чии са.
 *
 * `scope` не е филтър за удобство. Служителите на платформата и служителите на
 * клиентите се администрират по различни причини и почти никога в един и същ
 * момент; общ списък кани грешката да бъде замразен не този профил, който е
 * трябвало. Затова изгледът е един и същ компонент, но двата екрана са отделни
 * и всеки казва кого гледате.
 */
export function UsersView({
  signer,
  scope,
  onOpenMerchant,
}: {
  signer: Signer;
  scope: 'staff' | 'merchant';
  onOpenMerchant?: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const { data, error, loading, reload } = useLoad(
    () => api.users({ search: search || undefined, scope }),
    [search, scope],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function act(userId: string, transition: string): Promise<void> {
    setBusy(userId);
    setMsg(null);
    try {
      const res = await send(signer, `user:${userId}`, transition, 0, { reason: 'административно действие' });
      setMsg(
        transition === 'freeze'
          ? `Профилът е замразен. Прекратени сесии: ${res.new_version}.`
          : 'Профилът е размразен.',
      );
      await reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const staff = scope === 'staff';

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title={staff ? 'Само екипът на платформата' : 'Само хората на търговците'}>
        {staff ? (
          <>
            Тук са хората, които управляват WyoCSPay. Нито един профил на търговец не попада в този
            списък - те са в „Хора на търговците“. Какво може всеки от тях се задава в „Роли и права“.
          </>
        ) : (
          <>
            Тук са служителите на вашите клиенти, всеки с фирмата, на която принадлежи. Това не са
            вашите хора и ролите им нямат нищо общо с правата в тази конзола.
          </>
        )}
      </Note>

      <Note>
        Замразяването на потребител блокира входа <em>и</em> прекратява активните му сесии в същата
        подписана команда. Замразяване, което оставя съществуващите сесии да работят, не е замразяване.
      </Note>

      {msg ? <Note tone="warn">{msg}</Note> : null}

      <input
        className="wc-input"
        placeholder="Търси по име или имейл"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 320 }}
        aria-label="Търси по име или имейл"
      />

      <Frame loading={loading} error={error}>
        <Panel padded={false}>
          {data && data.data.length > 0 ? (
            <TableWrap>
              <table className="wc-table">
                <thead>
                  <tr>
                    <th>Име</th>
                    <th>Роля</th>
                    {staff ? null : <th>Търговец</th>}
                    <th>Сесии</th>
                    <th>Устройства</th>
                    <th>Статус</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <strong>{u.name}</strong>
                        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{u.email}</div>
                      </td>
                      <td className="wc-num" style={{ fontSize: 12 }}>{badgeLabel(u.role)}</td>
                      {staff ? null : (
                        <td style={{ fontSize: 13 }}>
                          {u.merchant && onOpenMerchant ? (
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => onOpenMerchant(u.merchant!.id)}
                            >
                              {u.merchant.name}
                            </button>
                          ) : (
                            (u.merchant?.name ?? '-')
                          )}
                        </td>
                      )}
                      <td className="wc-num">{u.sessions}</td>
                      <td className="wc-num">{u.devices}</td>
                      <td><Badge tone={u.status === 'ACTIVE' ? 'ok' : 'fail'}>{badgeLabel(u.status)}</Badge></td>
                      <td className="wc-right">
                        <button
                          type="button"
                          className={`wc-btn wc-btn-sm ${u.status === 'ACTIVE' ? 'wc-btn-danger' : 'wc-btn-secondary'}`}
                          disabled={busy === u.id}
                          onClick={() => act(u.id, u.status === 'ACTIVE' ? 'freeze' : 'unfreeze')}
                        >
                          {u.status === 'ACTIVE' ? 'Замрази' : 'Размрази'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Empty>
              {staff ? 'Няма служители, които да отговарят.' : 'Няма хора на търговци, които да отговарят.'}
            </Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function SettingsView({ signer }: { signer: Signer }) {
  const { data, error, loading, reload } = useLoad(() => api.settings());
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  async function save(key: string, value: unknown, version: number): Promise<void> {
    setBusy(key);
    setMsg(null);
    try {
      await send(signer, `setting:${key}`, 'update', version, { value });
      setMsg({ tone: 'ok', text: `${key} - обновено и записано.` });
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Всяка промяна е подписана команда">
        Настройките, отбелязани като <strong>чувствителни</strong>, променят как се вземат пари или кой
        може да извършва транзакции. Промяната се подписва от това устройство, носи версията, която сте
        заредили, и се записва в одитната верига със стойността преди и след.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <Frame loading={loading} error={error}>
        <div style={{ display: 'grid', gap: 12 }}>
          {data?.data.map((s) => (
            <Panel key={s.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: 14.5 }}>{settingLabel(s.key, s.label)}</strong>
                    {s.sensitive ? <Badge tone="warn">ЧУВСТВИТЕЛНА</Badge> : null}
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--wc-muted)' }}>{settingDescription(s.key, s.description)}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--wc-faint)' }}>
                    <span className="wc-num">{s.key}</span> · версия {s.version}
                    {s.updatedBy ? ` · last changed by ${shortId(s.updatedBy, 4)} ${relative(s.updatedAt)}` : ' · никога не е променяна'}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {s.type === 'boolean' ? (
                    <button
                      type="button"
                      className={`wc-btn wc-btn-sm ${s.value ? '' : 'wc-btn-secondary'}`}
                      disabled={busy === s.key}
                      onClick={() => save(s.key, !s.value, s.version)}
                    >
                      <ShieldIcon size={13} /> {s.value ? 'ВКЛ' : 'ИЗКЛ'}
                    </button>
                  ) : (
                    <>
                      <input
                        className="wc-input wc-num"
                        style={{ width: 130 }}
                        value={drafts[s.key] ?? String(s.value)}
                        onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
                        inputMode={s.type === 'number' ? 'numeric' : 'text'}
                      />
                      <button
                        type="button"
                        className="wc-btn wc-btn-sm"
                        disabled={busy === s.key || drafts[s.key] === undefined || drafts[s.key] === String(s.value)}
                        onClick={() =>
                          save(s.key, s.type === 'number' ? Number(drafts[s.key]) : drafts[s.key], s.version)
                        }
                      >
                        <ShieldIcon size={13} /> Подпиши и запази
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Panel>
          ))}
        </div>
      </Frame>
    </div>
  );
}
